/**
 * Custom API error class for consistent error handling
 */
export class ApiError extends Error {
    constructor(message, statusCode = 500, data = null) {
        super(message);
        this.name = "Error";
        this.statusCode = statusCode;
        this.data = data;
        // This is necessary to make instanceof work correctly in TypeScript
        Object.setPrototypeOf(this, ApiError.prototype);
    }
    /**
     * Convert to a standard error response object
     */
    toResponseObject() {
        return {
            success: false,
            error: this.message,
            statusCode: this.statusCode,
            data: this.data,
        };
    }
    /**
     * Create a 400 Bad Request error
     */
    static badRequest(message = "Bad Request", data = null) {
        return new ApiError(message, 400, data);
    }
    /**
     * Create a 401 Unauthorized error
     */
    static unauthorized(message = "Unauthorized", data = null) {
        return new ApiError(message, 401, data);
    }
    /**
     * Create a 403 Forbidden error
     */
    static forbidden(message = "Forbidden", data = null) {
        return new ApiError(message, 403, data);
    }
    /**
     * Create a 404 Not Found error
     */
    static notFound(message = "Not Found", data = null) {
        return new ApiError(message, 404, data);
    }
    /**
     * Create a 409 Conflict error
     */
    static conflict(message = "Conflict", data = null) {
        return new ApiError(message, 409, data);
    }
    /**
     * Create a 422 Unprocessable Entity error
     */
    static validationError(message = "Validation Error", data = null) {
        return new ApiError(message, 422, data);
    }
    /**
     * Create a 500 Internal Server Error
     */
    static internal(message = "Internal Server Error", data = null) {
        return new ApiError(message, 500, data);
    }
}
/**
 * Helper function to handle async route functions in Express
 */
export function asyncHandler(fn) {
    return async function (...args) {
        try {
            return await fn(...args);
        }
        catch (error) {
            const res = args[1];
            if (error instanceof ApiError) {
                return res.status(error.statusCode).json(error.toResponseObject());
            }
            console.error("Unhandled error:", error);
            const serverError = ApiError.internal();
            return res.status(serverError.statusCode).json(serverError.toResponseObject());
        }
    };
}
